import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CaretUp, CaretDown } from '@phosphor-icons/react';
import Menu from './menu';
import './style';

const Dropdown = (props) => {
  const {
    label,
    labelIcon,
    options,
    value,
    isMultiSelect,
    placeholder,
    onChange,
    width,
    className,
    showSelection,
    helpText,
    validationStatus,
    errorMessage, // added
    hasSearch,
    iconLeft,
    required,
    ...attr
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const getValue = () => {
    if (selectedValue?.length) {
      if (Array.isArray(selectedValue)) {
        return options?.filter((item) => selectedValue.includes(item?.id))?.map((opt) => opt?.label).join(', ');
      }

      return options?.find((item) => item?.id === selectedValue)?.label;
    }

    return placeholder;
  };

  const onClickMenuItem = (menu, e) => {
    e.stopPropagation();

    if (isMultiSelect) {
      const modifiedValues = [...selectedValue];

      if (modifiedValues.includes(menu?.id)) {
        const deleteIndex = modifiedValues.findIndex((item) => item === menu.id);
        modifiedValues.splice(deleteIndex, 1);
      } else modifiedValues.push(menu.id);

      setSelectedValue(modifiedValues);
    } else {
      setSelectedValue(menu?.id);
      onChange(menu?.id, menu);
      setIsOpen(false);
    }
  };

  const onDropdownToggle = () => {
    if (isOpen && isMultiSelect) {
      onChange(selectedValue);
      setIsOpen(false);
    } else {
      setIsOpen((prev) => !prev);
      if (hasSearch) setTimeout(() => searchRef?.current?.focus(), 200);
    }
  };

  const menuItems = useMemo(() => {
    if (searchText?.length) {
      return options?.filter((opt) => opt?.label?.toLowerCase()?.indexOf(searchText?.toLowerCase()) > -1);
    }

    return options;
  }, [options, searchText]);

  return (
    <div className={`dropdown-container-wrapper flex flex-col gap-2${validationStatus ? ` ${validationStatus}` : ''}`}>
      {label && (
        <label
          className={`label flex items-center gap-1.5 label-16 ${required ? 'required' : ''}`}
          htmlFor="dropdown-field"
        >
          {label}
          {labelIcon}
        </label>
      )}
      <div
        className={`dropdown-wrapper relative flex flex-col gap-1 `
        + `${isOpen ? 'open' : 'closed'} ${selectedValue?.length ? 'filled' : 'placeholder'} ${className}`}
        {...(width && ({ style: { width } }))}
        {...attr}
      >
        <div
          className="dropdown-selector h-full text-16 w-full flex items-center justify-between px-5 py-3"
          name="dropdown-field"
          ref={dropdownRef}
          onClick={onDropdownToggle}
        >
          <div className="flex gap-2 items-center">
            {iconLeft}
            {(hasSearch && isOpen) ? (
              <input
                ref={searchRef}
                className="search-input w-full"
                placeholder={getValue()}
                value={searchText}
                onChange={(e) => setSearchText(e?.target?.value)}
              />) : (
              <div
                className="dropdown-value"
              >
                {getValue()}
              </div>)}
          </div>
          {isOpen ? (
            <CaretUp size={24}
              color="#101010" />
          ) : (
            <CaretDown size={24}
              color="#101010" />
          )}
        </div>
        {isOpen && (
          <Menu
            menuItems={menuItems}
            onClick={onClickMenuItem}
            value={selectedValue}
            isMultiSelect={isMultiSelect}
            width={width}
            dropdownRef={dropdownRef}
            showSelection={showSelection}
            // revisit this when multi select is enabled
            onBlur={() => setIsOpen(false)}
          />
        )}
      </div>
      {validationStatus === 'error' && errorMessage ? (
        <div className="error-text text-12 text-red-600">{errorMessage}</div>
      ) : (
        helpText && <div className="help-text text-12 text-grey-70">{helpText}</div>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  label: PropTypes.string,
  labelIcon: PropTypes.node,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
  })),
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  isMultiSelect: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  showSelection: PropTypes.bool,
  required: PropTypes.bool,
  errorMessage: PropTypes.string, // added
};

Dropdown.defaultProps = {
  label: '',
  labelIcon: null,
  options: [],
  value: null,
  isMultiSelect: false,
  placeholder: 'Select',
  onChange: () => null,
  width: 640,
  className: '',
  showSelection: true,
  required: false,
  errorMessage: '', // added
};

export default Dropdown;